if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.zoom = 1;
        this.activeImage = null;
        this.baseImageWidth = null;
        this.baseImageHeight = null;

        this.onResize = () => {
          if (this.hasAttribute('open') && this.activeImage) {
            this.baseImageWidth = null;
            this.baseImageHeight = null;
            this.renderImage();
          }
        };
      }

      connectedCallback() {
        if (super.connectedCallback) {
          super.connectedCallback();
        }

        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);

        if (super.disconnectedCallback) {
          super.disconnectedCallback();
        }
      }

      hide() {
        if (this.zoomControls) {
          this.zoomControls.hidden = true;
          this.zoomControls.style.display = 'none';
        }

        this.activeImage = null;
        this.baseImageWidth = null;
        this.baseImageHeight = null;

        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      createControls() {
        if (this.zoomControls) return;

        const dialog =
          this.querySelector('[role="dialog"]') || this;

        const controls = document.createElement('div');

        controls.setAttribute('role', 'group');
        controls.setAttribute(
          'aria-label',
          'Foto vergroten of verkleinen'
        );

        controls.style.cssText = `
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 30;
          display: none;
          gap: 8px;
          align-items: center;
          padding: 8px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 12px #0003;
        `;

        const addButton = (text, label, action) => {
          const button = document.createElement('button');

          button.type = 'button';
          button.textContent = text;
          button.setAttribute('aria-label', label);

          button.style.cssText = `
            min-width: 44px;
            min-height: 44px;
            padding: 8px 12px;
            background: white;
            color: black;
            border: 1px solid #ccc;
            border-radius: 4px;
            cursor: pointer;
            font-size: 18px;
            touch-action: manipulation;
          `;

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            action();
          });
          // Laat de bediening van de zoomknoppen niet doorgeven
          // aan de muis-/aanraakafhandeling van het fotovenster.
          ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach(
            (eventName) => {
              controls.addEventListener(eventName, (event) => {
                event.stopPropagation();
              });
            }
          );
          controls.appendChild(button);
          return button;
        };

        this.minusButton = addButton(
          '−',
          'Uitzoomen',
          () => this.setZoom(this.zoom - 0.25)
        );

        addButton('Passend', 'Hele foto tonen', () => {
          this.setZoom(1);
        });

        this.plusButton = addButton(
          '+',
          'Inzoomen',
          () => this.setZoom(this.zoom + 0.25)
        );

        dialog.appendChild(controls);
        this.zoomControls = controls;

        const existingCloseButton = this.querySelector(
          '[id^="ModalClose-"], .product-media-modal__toggle'
        );

        if (existingCloseButton) {
          this.closeButton = existingCloseButton;
        } else {
          this.closeButton = document.createElement('button');
          this.closeButton.type = 'button';
          this.closeButton.textContent = '×';

          this.closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
          });

          dialog.appendChild(this.closeButton);
        }

        this.closeButton.setAttribute(
          'aria-label',
          'Fotovenster sluiten'
        );

        Object.assign(this.closeButton.style, {
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: '50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          padding: '0',
          background: 'white',
          color: 'black',
          border: '1px solid #ccc',
          borderRadius: '50%',
          fontSize: '32px',
          lineHeight: '1',
          cursor: 'pointer',
          touchAction: 'manipulation'
        });
      }

      setZoom(value) {
        if (!this.activeImage || !this.activeImage.naturalWidth) {
          return;
        }

        this.zoom = Math.min(4, Math.max(1, value));
        this.renderImage();
      }

      renderImage() {
        const image = this.activeImage;
        const container = this.querySelector('[role="document"]');

        if (!image || !container || !image.naturalWidth) {
          return;
        }

        const viewportHeight = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;

        container.style.setProperty(
          'height',
          `${viewportHeight}px`,
          'important'
        );

        container.style.setProperty(
          'max-height',
          `${viewportHeight}px`,
          'important'
        );

        container.style.setProperty('box-sizing', 'border-box');
        container.style.setProperty('overflow', 'auto', 'important');
        container.style.setProperty(
          'scroll-behavior',
          'auto',
          'important'
        );
        container.style.setProperty('scrollbar-gutter', 'stable');

        if (!this.baseImageWidth || !this.baseImageHeight) {
          const spacing = getComputedStyle(container);

          const horizontalPadding =
            (parseFloat(spacing.paddingLeft) || 0) +
            (parseFloat(spacing.paddingRight) || 0);

          const verticalPadding =
            (parseFloat(spacing.paddingTop) || 0) +
            (parseFloat(spacing.paddingBottom) || 0);

          const availableWidth =
            container.clientWidth - horizontalPadding - 20;

          const availableHeight =
            container.clientHeight - verticalPadding - 20;

          if (availableWidth <= 0 || availableHeight <= 0) {
            return;
          }

          const fitScale = Math.min(
            availableWidth / image.naturalWidth,
            availableHeight / image.naturalHeight,
            1
          );

          this.baseImageWidth = image.naturalWidth * fitScale;
          this.baseImageHeight = image.naturalHeight * fitScale;
        }

        const oldWidth = image.getBoundingClientRect().width;
        const oldHeight = image.getBoundingClientRect().height;

        const centerX = oldWidth
          ? (
              container.scrollLeft +
              container.clientWidth / 2 -
              image.offsetLeft
            ) / oldWidth
          : 0.5;

        const centerY = oldHeight
          ? (
              container.scrollTop +
              container.clientHeight / 2 -
              image.offsetTop
            ) / oldHeight
          : 0.5;

        const width = this.baseImageWidth * this.zoom;
        const height = this.baseImageHeight * this.zoom;

        const styles = {
          width: `${width}px`,
          height: `${height}px`,
          'max-width': 'none',
          'max-height': 'none',
          'min-width': '0',
          display: 'block',
          position: 'static',
          margin: '0 auto',
          'object-fit': 'contain',
          'box-sizing': 'border-box',
          transition: 'none'
        };

        Object.entries(styles).forEach(([property, value]) => {
          image.style.setProperty(property, value, 'important');
        });

        if (this.zoom === 1) {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        } else {
          container.scrollLeft =
            image.offsetLeft +
            centerX * width -
            container.clientWidth / 2;

          container.scrollTop =
            image.offsetTop +
            centerY * height -
            container.clientHeight / 2;
        }

        this.minusButton.disabled = this.zoom <= 1;
        this.plusButton.disabled = this.zoom >= 4;

        this.minusButton.style.opacity =
          this.minusButton.disabled ? '0.4' : '1';

        this.plusButton.style.opacity =
          this.plusButton.disabled ? '0.4' : '1';
      }

      showActiveMedia() {
        const mediaId =
          this.openedBy.getAttribute('data-media-id');

        const mediaElements = Array.from(
          this.querySelectorAll('[data-media-id]')
        );

        mediaElements.forEach((element) => {
          const isActive =
            element.getAttribute('data-media-id') === mediaId;

          element.classList.toggle('active', isActive);

          if (isActive) {
            element.style.removeProperty('display');
          } else {
            element.style.setProperty(
              'display',
              'none',
              'important'
            );
          }
        });

        this.baseImageWidth = null;
        this.baseImageHeight = null;

        const activeMedia = mediaElements.find(
          (element) =>
            element.getAttribute('data-media-id') === mediaId
        );

        if (!activeMedia) return;

        this.createControls();

        // Alleen echte foto's krijgen zoomknoppen.
        this.activeImage = activeMedia.matches('img')
          ? activeMedia
          : null;

        this.zoomControls.hidden = !this.activeImage;
        this.zoomControls.style.display =
          this.activeImage ? 'flex' : 'none';

        const container =
          this.querySelector('[role="document"]');

        if (!container) return;
        // Voorkom dat klikken op de scrollbalk het venster sluit.
        // Klikken op lege ruimte binnen dit gedeelte sluiten het
        // venster hiermee ook niet meer.
        if (!container.dataset.scrollEventsProtected) {
          ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click'].forEach(
            (eventName) => {
              container.addEventListener(eventName, (event) => {
                event.stopPropagation();
              });
            }
          );

          container.dataset.scrollEventsProtected = 'true';
        }

        if (this.activeImage) {
          const image = this.activeImage;

          container.style.setProperty('box-sizing', 'border-box');
          container.style.setProperty('width', '100%', 'important');
          container.style.setProperty('overflow', 'auto', 'important');
          container.style.setProperty(
            'padding',
            '72px 16px 88px',
            'important'
          );
          container.style.setProperty(
            'scroll-behavior',
            'auto',
            'important'
          );

          this.zoom = 1;

          // Voorkom zoomen voordat de foto geladen is.
          this.minusButton.disabled = true;
          this.plusButton.disabled = true;

          const fit = () => {
            if (this.activeImage !== image) return;

            this.renderImage();
            container.scrollTop = 0;
            container.scrollLeft = 0;
          };

          if (image.complete && image.naturalWidth) {
            requestAnimationFrame(fit);
          } else {
            image.addEventListener('load', fit, { once: true });
          }
        } else {
          [
            'height',
            'max-height',
            'width',
            'overflow',
            'padding',
            'box-sizing',
            'scroll-behavior',
            'scrollbar-gutter'
          ].forEach((property) => {
            container.style.removeProperty(property);
          });

          activeMedia.scrollIntoView();

          const template = activeMedia.querySelector('template');

          if (
            activeMedia.nodeName === 'DEFERRED-MEDIA' &&
            template &&
            template.content.querySelector('.js-youtube')
          ) {
            activeMedia.loadContent();
          }
        }
      }
    }
  );
}
